import { isCompedEntitlement } from './billingCopy';
import { isActivePaidAccountStatus } from './entitlementBillingErrors';
import type { LscBillingStatusResponse, LscCommissionerAccessResponse } from './types';

export type CommissionerAccessPresentationState =
  | 'unauthenticated'
  | 'free_member_blocked'
  | 'commissioner_eligible'
  | 'commissioner_paid_unbound'
  | 'commissioner_capacity_exhausted'
  | 'commissioner_subscription_inactive'
  | 'commissioner_comped'
  | 'billing_model_disabled'
  | 'unknown';

export type CommissionerAccessCta = {
  code: string;
  label: string;
  href?: string;
};

export type CommissionerAccessPresentation = {
  state: CommissionerAccessPresentationState;
  canCreateLeague: boolean;
  headline: string;
  detailLines: string[];
  primaryCta: CommissionerAccessCta | null;
  secondaryCtas: CommissionerAccessCta[];
  showCreateForm: boolean;
  showUpgradePanel: boolean;
  showManageBilling: boolean;
  showRenewBilling: boolean;
  showJoinPath: boolean;
  reasonCodes: string[];
};

export const COMMISSIONER_ACCESS_JOIN_HEADLINE = 'Join a league for free';
export const COMMISSIONER_ACCESS_START_HEADLINE = 'Start a league as commissioner';
export const COMMISSIONER_ACCESS_SUBSCRIPTION_REQUIRED =
  'Creating a league requires an active commissioner subscription.';
export const COMMISSIONER_ACCESS_PAID_UNBOUND_GUIDANCE =
  'Your subscription is active. After creating your league, link it in billing or league setup.';
export const COMMISSIONER_ACCESS_CAPACITY_EXHAUSTED =
  'Your subscription is already linked to the maximum number of leagues.';
export const COMMISSIONER_ACCESS_SUBSCRIPTION_INACTIVE =
  'Your commissioner subscription is inactive. Manage billing to restore league creation access.';
export const COMMISSIONER_ACCESS_JOIN_DETAIL =
  'Sign in with Twitch and enter an invite code from your commissioner to join immediately.';
export const COMMISSIONER_ACCESS_COMMISSIONER_DETAIL =
  'Commissioner plans start at $19.99/month. Subscribe to create and manage a league workspace.';

function isPaidUnboundAccount(
  access: LscCommissionerAccessResponse,
  billingStatus?: LscBillingStatusResponse | null
): boolean {
  const boundCount = access.bound_league_count ?? 0;
  const ownedCount = access.owned_league_count ?? 0;
  if (boundCount > 0 || ownedCount > 0) {
    return false;
  }
  if (billingStatus?.subscription_state === 'account_active_unbound') {
    return true;
  }
  if (billingStatus?.status === 'unbound' && billingStatus.account_subscription_active) {
    return true;
  }
  return isActivePaidAccountStatus({
    subscription_status: billingStatus?.subscription_status,
    entitlement: billingStatus?.entitlement ?? null,
  });
}

function isCompedCommissioner(
  access: LscCommissionerAccessResponse,
  billingStatus?: LscBillingStatusResponse | null
): boolean {
  if (access.billing_state === 'league_subscription_comped') {
    return true;
  }
  if (isCompedEntitlement(billingStatus?.entitlement)) {
    return true;
  }
  return false;
}

function buildJoinCtas(): CommissionerAccessCta[] {
  return [
    { code: 'join', label: 'Enter invite code', href: '/join' },
    { code: 'leagues', label: 'Go to leagues', href: '/leagues' },
  ];
}

function buildCheckoutCtas(): { primary: CommissionerAccessCta; secondary: CommissionerAccessCta[] } {
  return {
    primary: { code: 'pricing', label: 'View commissioner plans', href: '/pricing' },
    secondary: [{ code: 'billing', label: 'Manage billing', href: '/billing' }],
  };
}

function buildManageBillingCtas(): { primary: CommissionerAccessCta; secondary: CommissionerAccessCta[] } {
  return {
    primary: { code: 'billing', label: 'Manage billing', href: '/billing' },
    secondary: [{ code: 'pricing', label: 'View commissioner plans', href: '/pricing' }],
  };
}

/** Map backend commissioner-access (+ optional billing status) to customer-safe UI presentation. */
export function resolveCommissionerAccessPresentation(
  access: LscCommissionerAccessResponse | null,
  options?: {
    isAuthenticated?: boolean;
    billingStatus?: LscBillingStatusResponse | null;
  }
): CommissionerAccessPresentation {
  const isAuthenticated = options?.isAuthenticated ?? true;
  const billingStatus = options?.billingStatus ?? null;
  const reasonCodes: string[] = [];

  if (!isAuthenticated) {
    return {
      state: 'unauthenticated',
      canCreateLeague: false,
      headline: COMMISSIONER_ACCESS_JOIN_HEADLINE,
      detailLines: [COMMISSIONER_ACCESS_JOIN_DETAIL],
      primaryCta: { code: 'sign_in', label: 'Sign in with Twitch' },
      secondaryCtas: [{ code: 'join', label: 'Enter invite code', href: '/join' }],
      showCreateForm: false,
      showUpgradePanel: false,
      showManageBilling: false,
      showRenewBilling: false,
      showJoinPath: true,
      reasonCodes: ['unauthenticated'],
    };
  }

  if (!access) {
    return {
      state: 'unknown',
      canCreateLeague: false,
      headline: COMMISSIONER_ACCESS_START_HEADLINE,
      detailLines: ['Commissioner access is unavailable right now. Try again or contact support.'],
      primaryCta: { code: 'leagues', label: 'Back to leagues', href: '/leagues' },
      secondaryCtas: buildJoinCtas(),
      showCreateForm: false,
      showUpgradePanel: true,
      showManageBilling: false,
      showRenewBilling: false,
      showJoinPath: true,
      reasonCodes: ['access_unavailable'],
    };
  }

  reasonCodes.push(access.reason_code);

  if (access.reason_code === 'billing_model_disabled' && access.can_create_league) {
    return {
      state: 'billing_model_disabled',
      canCreateLeague: true,
      headline: COMMISSIONER_ACCESS_START_HEADLINE,
      detailLines: [],
      primaryCta: null,
      secondaryCtas: buildJoinCtas(),
      showCreateForm: true,
      showUpgradePanel: false,
      showManageBilling: false,
      showRenewBilling: false,
      showJoinPath: true,
      reasonCodes,
    };
  }

  // Backend (commissioner_access_service.py) always pairs this reason with can_create_league: false.
  if (access.reason_code === 'commissioner_capacity_exhausted') {
    const ctas = buildManageBillingCtas();
    return {
      state: 'commissioner_capacity_exhausted',
      canCreateLeague: false,
      headline: COMMISSIONER_ACCESS_START_HEADLINE,
      detailLines: [COMMISSIONER_ACCESS_CAPACITY_EXHAUSTED],
      primaryCta: ctas.primary,
      secondaryCtas: [...ctas.secondary, ...buildJoinCtas()],
      showCreateForm: false,
      showUpgradePanel: true,
      showManageBilling: true,
      showRenewBilling: false,
      showJoinPath: true,
      reasonCodes,
    };
  }

  // Backend (commissioner_access_service.py) always pairs this reason with can_create_league: false.
  if (access.reason_code === 'commissioner_subscription_inactive') {
    const ctas = buildManageBillingCtas();
    return {
      state: 'commissioner_subscription_inactive',
      canCreateLeague: false,
      headline: COMMISSIONER_ACCESS_START_HEADLINE,
      detailLines: [COMMISSIONER_ACCESS_SUBSCRIPTION_INACTIVE],
      primaryCta: ctas.primary,
      secondaryCtas: [...ctas.secondary, ...buildJoinCtas()],
      showCreateForm: false,
      showUpgradePanel: true,
      showManageBilling: false,
      showRenewBilling: true,
      showJoinPath: true,
      reasonCodes,
    };
  }

  if (
    !access.can_create_league &&
    (access.reason_code === 'league_creation_requires_subscription' ||
      access.reason_code === 'free_member_create_league_blocked')
  ) {
    const ctas = buildCheckoutCtas();
    return {
      state: 'free_member_blocked',
      canCreateLeague: false,
      headline: COMMISSIONER_ACCESS_START_HEADLINE,
      detailLines: [COMMISSIONER_ACCESS_SUBSCRIPTION_REQUIRED, COMMISSIONER_ACCESS_COMMISSIONER_DETAIL],
      primaryCta: access.cta?.label
        ? { code: 'pricing', label: access.cta.label, href: access.cta.href }
        : ctas.primary,
      secondaryCtas: [...buildJoinCtas(), ...ctas.secondary],
      showCreateForm: false,
      showUpgradePanel: true,
      showManageBilling: false,
      showRenewBilling: false,
      showJoinPath: true,
      reasonCodes,
    };
  }

  if (access.can_create_league && isCompedCommissioner(access, billingStatus)) {
    return {
      state: 'commissioner_comped',
      canCreateLeague: true,
      headline: COMMISSIONER_ACCESS_START_HEADLINE,
      detailLines: ['Complimentary commissioner access is active.'],
      primaryCta: null,
      secondaryCtas: buildJoinCtas(),
      showCreateForm: true,
      showUpgradePanel: false,
      showManageBilling: false,
      showRenewBilling: false,
      showJoinPath: true,
      reasonCodes,
    };
  }

  if (access.can_create_league && isPaidUnboundAccount(access, billingStatus)) {
    return {
      state: 'commissioner_paid_unbound',
      canCreateLeague: true,
      headline: COMMISSIONER_ACCESS_START_HEADLINE,
      detailLines: [COMMISSIONER_ACCESS_PAID_UNBOUND_GUIDANCE],
      primaryCta: { code: 'billing', label: 'Link subscription in billing', href: '/billing' },
      secondaryCtas: buildJoinCtas(),
      showCreateForm: true,
      showUpgradePanel: false,
      showManageBilling: false,
      showRenewBilling: false,
      showJoinPath: true,
      reasonCodes: [...reasonCodes, 'paid_unbound_derived'],
    };
  }

  if (access.can_create_league) {
    return {
      state: 'commissioner_eligible',
      canCreateLeague: true,
      headline: COMMISSIONER_ACCESS_START_HEADLINE,
      detailLines: [],
      primaryCta: null,
      secondaryCtas: buildJoinCtas(),
      showCreateForm: true,
      showUpgradePanel: false,
      showManageBilling: false,
      showRenewBilling: false,
      showJoinPath: true,
      reasonCodes,
    };
  }

  const ctas = buildCheckoutCtas();
  return {
    state: 'unknown',
    canCreateLeague: false,
    headline: COMMISSIONER_ACCESS_START_HEADLINE,
    detailLines: [COMMISSIONER_ACCESS_SUBSCRIPTION_REQUIRED],
    primaryCta: ctas.primary,
    secondaryCtas: [...buildJoinCtas(), ...ctas.secondary],
    showCreateForm: false,
    showUpgradePanel: true,
    showManageBilling: false,
    showRenewBilling: false,
    showJoinPath: true,
    reasonCodes: [...reasonCodes, 'fallback_blocked'],
  };
}

export function shouldShowCreateLeagueNav(presentation: CommissionerAccessPresentation): boolean {
  return (
    presentation.canCreateLeague ||
    presentation.state === 'billing_model_disabled' ||
    presentation.state === 'unknown'
  );
}
